
import { useState } from 'react'
import { Link } from 'wouter'
import useSWR from 'swr'
import { cn } from '@/lib/utils'
import {
  Database, KeyRound, Copy, CheckCheck, Trash2, Power,
  PowerOff, Plus, Terminal, RefreshCw, AlertTriangle,
  Shield, TableProperties, Code2, Globe, Braces, FileCode2,
  Layers, Link2, ExternalLink, ChevronRight, ChevronDown,
  Eye, EyeOff, Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

const fetcher = async (url: string) => {
  const res = await fetch(url, { credentials: 'include' })
  return res.json() as Promise<any>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function CopyButton({ text, size = 'sm' }: { text: string; size?: 'sm' | 'md' }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async e => {
        e.stopPropagation()
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
      className={cn(
        'rounded transition-colors text-muted-foreground hover:text-foreground flex-shrink-0',
        size === 'sm' ? 'p-1.5 hover:bg-muted/80' : 'p-2 hover:bg-muted'
      )}
      title="Copy to clipboard"
    >
      {copied
        ? <CheckCheck className="w-3.5 h-3.5 text-success" />
        : <Copy className="w-3.5 h-3.5" />
      }
    </button>
  )
}

function StringRow({ label, value, masked = false, mono = true }: { label: string; value: string; masked?: boolean; mono?: boolean }) {
  const [show, setShow] = useState(!masked)
  const display = show ? value : value.replace(/[^@/?&=:]/g, '•').slice(0, 56) + '...'
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
        {masked && (
          <button onClick={() => setShow(v => !v)} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {show ? 'Hide' : 'Reveal'}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 group">
        <code className={cn('flex-1 text-[12px] text-foreground overflow-x-auto whitespace-nowrap', mono && 'font-mono')}>
          {display}
        </code>
        <CopyButton text={value} />
      </div>
    </div>
  )
}

function CodeBlock({ code, language = 'bash' }: { code: string; language?: string }) {
  return (
    <div className="relative rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/60 border-b border-border">
        <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{language}</span>
        <CopyButton text={code} />
      </div>
      <pre className="px-4 py-3 text-[12px] font-mono text-foreground overflow-x-auto leading-relaxed bg-muted/20">
        <code>{code}</code>
      </pre>
    </div>
  )
}

function relativeTime(d: string | null) {
  if (!d) return 'Never'
  const diff = Date.now() - new Date(d).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ApiKey = {
  id: string
  name: string
  fingerprint: string
  permissions: string
  is_active: number
  request_count: number
  last_used_at: string | null
  created_at: string
  expires_at: string | null
}

type TableInfo = {
  name: string
  row_count: number
  sql: string
}

type ConnectInfo = {
  endpoint: string
  databaseUrl: string
  keyHint: string
  snippets: Record<string, string>
}

// ─── Key card ─────────────────────────────────────────────────────────────────

function KeyCard({ apiKey, onMutate }: { apiKey: ApiKey; onMutate: () => void }) {
  const perms: string[] = JSON.parse(apiKey.permissions ?? '[]')
  const isActive = Boolean(apiKey.is_active)

  async function toggle() {
    await fetch(`/api/keys/${apiKey.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_active: isActive ? 0 : 1 }),
    })
    onMutate()
  }

  async function revoke() {
    await fetch(`/api/keys/${apiKey.id}`, { 
      method: 'DELETE',
      credentials: 'include'
    })
    onMutate()
  }

  return (
    <div className={cn(
      'rounded-xl border bg-card p-4 transition-all',
      isActive ? 'border-border hover:border-lava/30' : 'border-border/50 opacity-60'
    )}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="p-1.5 rounded bg-lava/10 flex-shrink-0">
              <KeyRound className="w-3 h-3 text-lava" />
            </div>
            <span className="font-medium text-sm text-foreground truncate">{apiKey.name}</span>
            <Badge
              variant="outline"
              className={cn(
                'text-[10px] h-4 shrink-0',
                isActive ? 'border-success/40 text-success' : 'border-muted-foreground/30 text-muted-foreground'
              )}
            >
              {isActive ? 'Active' : 'Disabled'}
            </Badge>
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground bg-muted/40 rounded px-2 py-1 w-fit">
            <span>lfk_v1_...{apiKey.fingerprint.slice(-8)}</span>
            <CopyButton text={`lfk_v1_...${apiKey.fingerprint}`} size="sm" />
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={toggle}
            className="p-1.5 rounded hover:bg-muted/80 transition-colors text-muted-foreground hover:text-foreground"
            title={isActive ? 'Disable key' : 'Enable key'}
          >
            {isActive
              ? <Power className="w-3.5 h-3.5 text-success" />
              : <PowerOff className="w-3.5 h-3.5" />
            }
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="p-1.5 rounded hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-sm">Revoke API key?</AlertDialogTitle>
                <AlertDialogDescription className="text-xs">
                  {apiKey.name} will stop working immediately. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="text-xs h-8">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={revoke}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs h-8"
                >
                  Revoke key
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
        <div>
          <p className="text-[9px] uppercase tracking-wider mb-0.5 text-muted-foreground/60">Requests</p>
          <p className="font-mono text-foreground">{apiKey.request_count.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider mb-0.5 text-muted-foreground/60">Last used</p>
          <p>{relativeTime(apiKey.last_used_at)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider mb-0.5 text-muted-foreground/60">Created</p>
          <p>{relativeTime(apiKey.created_at)}</p>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {perms.map(p => (
          <Badge key={p} variant="secondary" className="text-[9px] h-4 font-mono px-1.5">
            {p}
          </Badge>
        ))}
      </div>
    </div>
  )
}

// ─── Table schema row ──────────────────────────────────────────────────────────

function TableRow({ table }: { table: TableInfo }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2.5 w-full px-3 py-2.5 bg-muted/30 hover:bg-muted/60 transition-colors text-left"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
        <TableProperties className="w-3.5 h-3.5 text-lava flex-shrink-0" />
        <span className="text-xs font-mono font-semibold text-foreground">{table.name}</span>
        <Badge variant="outline" className="ml-auto text-[9px] h-4 px-1.5 border-border text-muted-foreground font-mono">
          {table.row_count.toLocaleString()} rows
        </Badge>
      </button>
      {open && (
        <div className="p-3 bg-card border-t border-border">
          <pre className="text-[10px] font-mono text-muted-foreground whitespace-pre-wrap leading-relaxed overflow-x-auto">
            {table.sql}
          </pre>
        </div>
      )}
    </div>
  )
}

// ─── SDK snippets ──────────────────────────────────────────────────────────────

const SDK_TABS = [
  { id: 'drizzle',          label: 'Drizzle ORM',  icon: Layers },
  { id: 'typescript',       label: 'TypeScript',   icon: Braces },
  { id: 'neonCompat',       label: 'Neon compat',  icon: Code2 },
  { id: 'httpRest',         label: 'REST / cURL',  icon: Terminal },
  { id: 'python',           label: 'Python',       icon: FileCode2 },
  { id: 'cloudflareWorker', label: 'CF Worker',    icon: Globe },
]

const SDK_LANG: Record<string, string> = {
  drizzle:          'typescript',
  typescript:       'typescript',
  neonCompat:       'typescript',
  httpRest:         'bash',
  python:           'python',
  cloudflareWorker: 'typescript',
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function PortalPage() {
  const { data: keysData, error: keysError, isLoading: keysLoading, mutate: mutateKeys } =
    useSWR<{ keys: ApiKey[] }>('/api/keys', fetcher)

  const { data: dbInfo, isLoading: dbLoading } =
    useSWR<{ tables: TableInfo[]; latency_ms: number; healthy: boolean }>('/api/database/info', fetcher)

  const { data: connectInfo, isLoading: connectLoading } =
    useSWR<ConnectInfo>('/api/connect', fetcher)

  const [activeTab, setActiveTab] = useState('connect')
  const [sdkTab, setSdkTab] = useState('drizzle')

  const keys = keysData?.keys ?? []
  const tables = dbInfo?.tables ?? []
  const snippets = connectInfo?.snippets ?? {}
  const endpoint = connectInfo?.endpoint ?? ''
  const databaseUrl = connectInfo?.databaseUrl ?? ''

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-foreground">Project Portal</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Connection strings, API keys, SQL console, and schema explorer
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
                dbInfo?.healthy
                  ? 'bg-success/10 border-success/30 text-success'
                  : 'bg-muted border-border text-muted-foreground'
              )}>
                <span className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  dbInfo?.healthy ? 'bg-success animate-pulse' : 'bg-muted-foreground'
                )} />
                {dbInfo?.healthy ? `D1 Connected · ${dbInfo.latency_ms}ms` : 'Checking D1...'}
              </div>
              <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs h-8 border-border">
                <Link href="/playground">
                  <Terminal className="w-3.5 h-3.5" />
                  SQL Console
                </Link>
              </Button>
              <Button asChild size="sm" className="gap-1.5 text-xs h-8 lava-gradient text-lava-foreground border-0 hover:opacity-90">
                <Link href="/dashboard/keys">
                  <Plus className="w-3.5 h-3.5" />
                  New API Key
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 h-9 bg-muted border border-border p-0.5">
            <TabsTrigger value="connect" className="gap-1.5 text-xs h-8 data-[state=active]:bg-card">
              <Link2 className="w-3.5 h-3.5" /> Connect
            </TabsTrigger>
            <TabsTrigger value="sdk" className="gap-1.5 text-xs h-8 data-[state=active]:bg-card">
              <Code2 className="w-3.5 h-3.5" /> SDK
            </TabsTrigger>
            <TabsTrigger value="schema" className="gap-1.5 text-xs h-8 data-[state=active]:bg-card">
              <TableProperties className="w-3.5 h-3.5" /> Schema
            </TabsTrigger>
            <TabsTrigger value="keys" className="gap-1.5 text-xs h-8 data-[state=active]:bg-card">
              <KeyRound className="w-3.5 h-3.5" /> Keys
              {keys.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-lava/15 text-lava font-medium">
                  {keys.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Connect Tab ── */}
          <TabsContent value="connect" className="space-y-5">
            {/* DATABASE_URL — the hero element */}
            <div className="rounded-xl border border-lava/20 bg-lava/5 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-lava/15">
                  <Database className="w-4 h-4 text-lava" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground">DATABASE_URL</p>
                  <p className="text-xs text-muted-foreground">Drop this into your .env — works with Prisma, Drizzle, SQLAlchemy, and any pg-compatible driver</p>
                </div>
              </div>
              <StringRow
                label="DATABASE_URL — paste into your .env file"
                value={databaseUrl || `postgresql://[email]:[YOUR_API_KEY]@lavaflowstreams.pro/lavaflow?sslmode=require`}
                masked
              />
              <p className="mt-2.5 text-[11px] text-muted-foreground">
                Replace <code className="font-mono text-lava bg-lava/10 px-1 rounded">{'${LAVAFLOW_SECRET}'}</code> with your actual API key from the Keys tab. The key is your password — never commit it to source control.
              </p>
            </div>

            {/* Connection parameters */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="font-medium text-sm mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-accent" />
                Connection Parameters
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StringRow label="Host" value={`lavaflowstreams.pro`} />
                <StringRow label="Port" value="443 (HTTPS)" />
                <StringRow label="Database" value="lavaflow" />
                <StringRow label="SSL Mode" value="require" />
                <StringRow label="REST Endpoint (raw fetch)" value={endpoint || 'https://d1-rest-lavaflowstreams.jxrstudios-damascusai.workers.dev'} />
                <StringRow label="AI Gateway" value={`https://lavaflowstreams.pro/api/gateway`} />
              </div>
            </div>

            {/* ORM compatibility */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="font-medium text-sm mb-4">ORM Compatibility</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { name: 'Drizzle ORM',  note: 'sqlite-proxy adapter',       status: 'first-class' },
                  { name: 'Prisma',        note: 'D1 HTTP proxy driver',       status: 'supported' },
                  { name: 'TypeORM',       note: 'better-sqlite3-proxy',       status: 'supported' },
                  { name: 'Kysely',        note: 'sqlite-proxy dialect',       status: 'supported' },
                  { name: 'Raw fetch',     note: 'zero deps, any runtime',     status: 'first-class' },
                  { name: 'Neon compat',  note: 'sql`` tagged template',       status: 'first-class' },
                ].map(item => (
                  <div
                    key={item.name}
                    className="flex flex-col gap-0.5 p-3 rounded-lg bg-muted/40 border border-border"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={cn(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        item.status === 'first-class' ? 'bg-success' : 'bg-warning'
                      )} />
                      <span className="text-xs font-medium text-foreground">{item.name}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground pl-3">{item.note}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ── SDK Tab ── */}
          <TabsContent value="sdk" className="space-y-5">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-cyan-accent/10">
                  <Code2 className="w-4 h-4 text-cyan-accent" />
                </div>
                <div>
                  <p className="font-medium text-sm">SDK Integration Snippets</p>
                  <p className="text-xs text-muted-foreground">
                    Works with every major ORM, runtime, and language
                  </p>
                </div>
              </div>

              {/* SDK tab switcher */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {SDK_TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSdkTab(tab.id)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors border',
                      sdkTab === tab.id
                        ? 'bg-lava text-lava-foreground border-lava'
                        : 'bg-muted/60 text-muted-foreground border-border hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <tab.icon className="w-3 h-3" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {connectLoading ? (
                <div className="h-40 rounded-lg bg-muted/40 animate-pulse" />
              ) : snippets[sdkTab] ? (
                <CodeBlock code={snippets[sdkTab]} language={SDK_LANG[sdkTab]} />
              ) : (
                <div className="h-20 flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                  No snippet available — create an API key first
                </div>
              )}

              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-muted/40 border border-border">
                <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Set <code className="font-mono text-lava">LAVAFLOW_SECRET</code> in your environment variables to the API key from the{' '}
                  <button onClick={() => setActiveTab('keys')} className="text-lava underline underline-offset-2">
                    Keys tab
                  </button>.
                  Your key is shown once at creation and never stored in plaintext.
                </p>
              </div>
            </div>

            {/* Quick install */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="font-medium text-sm mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-lava" />
                Quick Install
              </p>
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">npm / Node.js</p>
                  <CodeBlock code={`npm install @lavaflow/client drizzle-orm\n# or\npnpm add @lavaflow/client drizzle-orm`} language="bash" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Python</p>
                  <CodeBlock code={`pip install lavaflow-python httpx`} language="bash" />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ── Schema Tab ── */}
          <TabsContent value="schema" className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium">Database Schema</p>
                <p className="text-xs text-muted-foreground">
                  {tables.length} tables · live from your D1 instance
                </p>
              </div>
              <Button asChild size="sm" variant="outline" className="gap-1.5 text-xs h-8 border-border">
                <Link href="/playground">
                  <Terminal className="w-3.5 h-3.5" />
                  Run SQL
                </Link>
              </Button>
            </div>

            {/* Stats */}
            {dbInfo && (
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Tables</p>
                  <p className="text-xl font-bold text-foreground tabular-nums">{tables.length}</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Total Rows</p>
                  <p className="text-xl font-bold text-foreground tabular-nums">
                    {tables.reduce((s, t) => s + t.row_count, 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Latency</p>
                  <p className="text-xl font-bold text-foreground tabular-nums">{dbInfo.latency_ms}ms</p>
                </div>
              </div>
            )}

            {dbLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 rounded-xl bg-muted/40 animate-pulse" />
                ))}
              </div>
            ) : tables.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-10 text-center">
                <Database className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">No tables yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Run your first <code className="font-mono text-lava">CREATE TABLE</code> in the{' '}
                  <Link href="/playground" className="text-lava underline underline-offset-2">
                    SQL Console
                  </Link>
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {tables.map(table => (
                  <TableRow key={table.name} table={table} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Keys Tab ── */}
          <TabsContent value="keys" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">API Keys</p>
                <p className="text-xs text-muted-foreground">
                  Your API key is the password in your DATABASE_URL and Bearer token
                </p>
              </div>
              <Button asChild size="sm" className="gap-1.5 text-xs h-8 lava-gradient text-lava-foreground border-0 hover:opacity-90">
                <Link href="/dashboard/keys">
                  <Plus className="w-3.5 h-3.5" />
                  Generate Key
                </Link>
              </Button>
            </div>

            {/* Security notice */}
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-cyan-accent/5 border border-cyan-accent/20">
              <Shield className="w-4 h-4 text-cyan-accent flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Keys are signed with Web Crypto HMAC-SHA256. Only a SHA-256 hash is stored — the raw key is shown once and never retrievable. Use it as the password in your DATABASE_URL or as the Bearer token.
              </p>
            </div>

            {keysLoading ? (
              <div className="space-y-2">
                {[1, 2].map(i => <div key={i} className="h-28 rounded-xl bg-muted/40 animate-pulse" />)}
              </div>
            ) : keysError ? (
              <div className="flex items-center gap-2 p-4 rounded-xl border border-destructive/20 bg-destructive/5">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <p className="text-sm text-destructive">Failed to load keys</p>
              </div>
            ) : keys.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-10 text-center">
                <KeyRound className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">No API keys yet</p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">
                  Generate a key to get your DATABASE_URL and start connecting
                </p>
                <Button asChild size="sm" className="gap-1.5 lava-gradient text-lava-foreground border-0 hover:opacity-90">
                  <Link href="/dashboard/keys">
                    <Plus className="w-3.5 h-3.5" />
                    Generate First Key
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {keys.map(k => (
                  <KeyCard key={k.id} apiKey={k} onMutate={mutateKeys} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
